import React from 'react';
import { Drawer, DrawerTrigger, DrawerContent, DrawerBody } from '@fiverr-private/overlay';
import { Container, Divider, Stack } from '@fiverr-private/layout_components';
import { translate } from '@fiverr-private/i18n-react';
import { Text } from '@fiverr-private/typography';
import { CollectionMenuOption } from '../../../types';
import { CollectionLabel } from '../CollectionLabel';
import { CreateListButton } from '../../CreateListButton/CreateListButton';
import { DrawerMenuItem } from './DrawerMenuItem';

interface SaveToListDrawerProps {
    collections: CollectionMenuOption[];
    trigger: React.ReactNode;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onChange: (collection: CollectionMenuOption) => void;
    onClickCreateList?: () => void;
}

// Adjust the scrollbar position so it doesn't overlap the checkboxes.
const PADDING_COMPENSATION = '24px';

const PaddingCompensation = ({ children }: { children: React.ReactNode }) => (
    <Container
        style={{
            marginRight: `-${PADDING_COMPENSATION}`,
            marginBottom: `-${PADDING_COMPENSATION}`,
            marginLeft: `-${PADDING_COMPENSATION}`,
        }}
    >
        {children}
    </Container>
);

const MenuBody = ({ collections, onChange }: Pick<SaveToListDrawerProps, 'collections' | 'onChange'>) => (
    <Container
        overflow="auto"
        maxHeight="360px"
        paddingY="2"
        style={{
            paddingLeft: PADDING_COMPENSATION,
            paddingRight: PADDING_COMPENSATION,
        }}
    >
        {collections.map((collection) => (
            <DrawerMenuItem
                key={collection.id}
                checked={collection.checked}
                onChange={(e) => onChange({ ...collection, checked: e.target.checked })}
            >
                <CollectionLabel collection={collection} />
            </DrawerMenuItem>
        ))}
    </Container>
);

export const SaveToListDrawer = ({
    collections,
    trigger,
    isOpen,
    onOpenChange,
    onChange,
    onClickCreateList,
}: SaveToListDrawerProps) => {
    const handleChange = (collection: CollectionMenuOption) => {
        onChange(collection);
        onOpenChange(false);
    };

    const handleClickCreateList = () => {
        onClickCreateList?.();
        onOpenChange(false);
    };

    const handleOpen = () => {
        onOpenChange(true);
    };

    const handleClose = () => {
        onOpenChange(false);
    };

    return (
        <Drawer isOpen={isOpen} onOpen={handleOpen} onClose={handleClose}>
            <DrawerTrigger>{trigger}</DrawerTrigger>
            <DrawerContent position="bottom">
                <DrawerBody>
                    <PaddingCompensation>
                        <Container background="white" width="100%">
                            <Stack paddingX="6" justifyContent="spaceBetween">
                                <Text fontWeight="semibold">
                                    {translate('collect_actions.lists_popover.your_lists')}
                                </Text>
                                <Container style={{ marginRight: '-2px' }}>
                                    <CreateListButton onClick={handleClickCreateList} />
                                </Container>
                            </Stack>
                            <Container marginTop="2">
                                <Divider />
                            </Container>
                        </Container>
                        <MenuBody collections={collections} onChange={handleChange} />
                    </PaddingCompensation>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};
